import { useEffect, useState } from "react"
import { api } from "../../api/client"
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts"
import { RefreshCw, DollarSign, Plus, Trash2, Server, Calendar, Clock } from "lucide-react"

export default function ConvertAdmin() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [newKey, setNewKey] = useState("")
  const [adding, setAdding] = useState(false)
  const [tab, setTab] = useState("24h") // "24h", "current", "last"

  const loadData = async () => {
    setLoading(true)
    try {
      const r = await api.get("/api/admin/convert")
      setData(r)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    const int = setInterval(loadData, 60000)
    return () => clearInterval(int)
  }, [])

  const handleAddKey = async (e) => {
    e.preventDefault()
    if (!newKey.trim()) return
    setAdding(true)
    try {
      await api.post("/api/admin/convert/keys", { key: newKey.trim() })
      setNewKey("")
      loadData()
    } catch (err) {
      alert(err.response?.data?.error || "Error agregando API")
    } finally {
      setAdding(false)
    }
  }

  const handleDeleteKey = async (id) => {
    if (!confirm("¿Eliminar esta API?")) return
    try {
      await api.del(`/api/admin/convert/keys/${id}`)
      loadData()
    } catch (_err) {
      alert("Error eliminando API")
    }
  }

  if (loading && !data) return <div className="p-10 text-center text-surface-500">Cargando...</div>

  const latestRate = data?.latest?.rate || 0
  const apiKeys = data?.api_keys || []
  const stats = data?.api_stats || {}

  // Formatear datos del gráfico según pestaña
  let chartData = []
  let xAxisKey = "time"
  
  if (tab === "24h") {
    chartData = (data?.history_24h || []).map(h => ({
      time: new Date(h.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      rate: h.rate
    }))
  } else if (tab === "current") {
    chartData = (data?.current_month || []).map(d => ({
      time: new Date(d.date).toLocaleDateString([], { day: '2-digit', month: 'short' }),
      rate: d.rate
    }))
  } else {
    chartData = (data?.last_month || []).map(d => ({
      time: new Date(d.date).toLocaleDateString([], { day: '2-digit', month: 'short' }),
      rate: d.rate
    }))
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Monitor de Divisas (USD/PEN)</h1>
          <p className="text-sm text-surface-500">Consulta rotativa (Round-Robin) para evitar límites de API.</p>
        </div>
        <button onClick={loadData} className="btn btn-secondary flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tarjeta de Precio */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-surface-200 flex flex-col justify-center">
          <div className="flex items-center gap-3 text-surface-500 mb-2">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            <span className="font-semibold text-sm">Último Precio (PEN)</span>
          </div>
          <div className="text-5xl font-extrabold text-surface-900">S/ {latestRate.toFixed(4)}</div>
          <div className="text-xs text-surface-400 mt-3">
            Actualizado: {data?.latest ? new Date(data.latest.created_at).toLocaleString() : 'N/A'}
          </div>
        </div>

        {/* Gestión de APIs */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-surface-200 lg:col-span-2">
          <div className="flex items-center gap-3 text-surface-500 mb-4">
            <Server className="w-5 h-5 text-brand-500" />
            <span className="font-semibold text-sm">Gestión de APIs (Rotación de Pila)</span>
          </div>
          
          <form onSubmit={handleAddKey} className="flex gap-2 mb-4">
            <input 
              type="text" 
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="Pegar nueva API Key..." 
              className="flex-1 px-3 py-2 border border-surface-300 rounded-lg text-sm focus:outline-none focus:border-brand-500"
            />
            <button type="submit" disabled={adding || !newKey.trim()} className="bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 flex items-center gap-1">
              <Plus className="w-4 h-4" /> Agregar
            </button>
          </form>

          <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
            {apiKeys.map((k, i) => (
              <div key={k.id} className="flex justify-between items-center bg-surface-50 p-3 rounded-xl border border-surface-100">
                <div>
                  <div className="text-sm font-bold text-surface-800">{k.masked_key} {i === 0 && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full ml-2">Siguiente en la pila</span>}</div>
                  <div className="text-xs text-surface-500">{stats[k.masked_key] || 0} consultas exitosas</div>
                </div>
                <button onClick={() => handleDeleteKey(k.id)} className="text-danger p-2 hover:bg-danger/10 rounded-lg transition-colors" title="Eliminar API">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {apiKeys.length === 0 && <div className="text-sm text-surface-400 text-center py-2">No hay APIs configuradas.</div>}
          </div>
        </div>
      </div>

      {/* Gráfico con Pestañas */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-surface-200">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-surface-800">Historial de Tasas</h3>
          <div className="flex bg-surface-100 p-1 rounded-xl">
            <button onClick={() => setTab("24h")} className={`px-4 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-2 ${tab === '24h' ? 'bg-white shadow-sm text-brand-600' : 'text-surface-500 hover:text-surface-700'}`}>
              <Clock className="w-4 h-4" /> 24 Horas
            </button>
            <button onClick={() => setTab("current")} className={`px-4 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-2 ${tab === 'current' ? 'bg-white shadow-sm text-brand-600' : 'text-surface-500 hover:text-surface-700'}`}>
              <Calendar className="w-4 h-4" /> Mes Actual
            </button>
            <button onClick={() => setTab("last")} className={`px-4 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-2 ${tab === 'last' ? 'bg-white shadow-sm text-brand-600' : 'text-surface-500 hover:text-surface-700'}`}>
              <Calendar className="w-4 h-4" /> Mes Anterior
            </button>
          </div>
        </div>
        
        <div className="h-80 w-full">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey={xAxisKey} tick={{fontSize: 12}} stroke="#94a3b8" />
                <YAxis domain={['auto', 'auto']} tick={{fontSize: 12}} stroke="#94a3b8" tickFormatter={(v) => `S/ ${v.toFixed(3)}`} />
                <Tooltip formatter={(value) => [`S/ ${value.toFixed(4)}`, 'Precio']} labelStyle={{color: '#64748b'}} />
                <Area type="monotone" dataKey="rate" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorRate)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-surface-400">No hay datos suficientes para este período.</div>
          )}
        </div>
      </div>
    </div>
  )
}
